import { Suspense, lazy, useState } from 'react';

import { ErrorBoundary } from 'react-error-boundary';

import { Button, Icon, Link } from '@vp/swan';
import { useLocalizations } from '@vp/ubik-localizations-provider';

import { usePdpSelectedOptions } from '../../../hooks/usePdpSelectedOptions';
import { trackSampleLinkOpened } from '../../../infrastructure/clients/client-side/trackingClient';
import { SampleDetails, SampleFlyoutData } from '../types/sampleFlyoutData';

import { AuthProvider } from './contexts/AuthProvider';
import { ProductProvider } from './contexts/ProductContext';

const SamplesFlyout = lazy(() =>
  import('./SamplesFlyout').then((m) => ({
    default: m.SamplesFlyout,
  }))
);

export type TriggerProps = {
  iconType: Pick<React.ComponentPropsWithoutRef<typeof Icon>, 'iconType'>['iconType'];
  label: string;
  onClick?: () => void;
};

/**
 * Props for the Samples component.
 *
 * @property {string} tenant - The tenant used for sample-navigation links.
 * @property {string} locale - The locale string (e.g., "en-US").
 * @property {string} cartTenant - The cart tenant used by cart operations in the flyout.
 * @property {SampleFlyoutData} productInformation - The product information and sample data to display,
 *  can be fetched by the data fetcher exported under /data in this package.
 * @property {React.FC<TriggerProps>} [trigger] - Optional. Custom trigger component used to open the flyout.
 *  If not provided, a default "Samples" link-style button is rendered.
 */
export type SamplesProps = {
  tenant: string;
  locale: string;
  cartTenant: string;
  productInformation: SampleFlyoutData;
  trigger?: React.FC<TriggerProps>;
};

const SamplesInner: React.FC<{
  tenant: string;
  locale: string;
  cartTenant: string;
  product: SampleDetails;
  trigger?: React.FC<TriggerProps>;
}> = ({ tenant, locale, cartTenant, product, trigger }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useLocalizations();
  const pdpSelectedOptions = usePdpSelectedOptions();
  const triggerOnClick = () => {
    setIsOpen(true);
    trackSampleLinkOpened(product);
  };

  const fallbackTrigger = (
    <Link
      withIcon
      render={(p) => (
        <Button skin="link" onClick={triggerOnClick} className={p.className}>
          {p.children}
        </Button>
      )}
    >
      <Icon iconType="samples" marginRight="between-icon-and-text" />
      {t('sample-button')}
    </Link>
  );

  return (
    <>
      {trigger
        ? trigger({
            iconType: 'samples',
            label: t('sample-button'),
            onClick: triggerOnClick,
          })
        : fallbackTrigger}
      {isOpen && (
        <ErrorBoundary fallback={null} onError={() => setIsOpen(false)}>
          <Suspense fallback={null}>
            <SamplesFlyout
              isOpen={isOpen}
              setIsOpen={setIsOpen}
              tenant={tenant}
              locale={locale}
              cartTenant={cartTenant}
              product={product}
              pdpSelectedOptions={pdpSelectedOptions}
            />
          </Suspense>
        </ErrorBoundary>
      )}
    </>
  );
};

export const Samples: React.FC<SamplesProps> = ({
  tenant,
  locale,
  cartTenant,
  productInformation,
  trigger,
}) => {
  if (!productInformation.isEnabled || !productInformation.sampleDetails) {
    return null;
  }
  return (
    <AuthProvider>
      <ProductProvider>
        <SamplesInner
          trigger={trigger}
          tenant={tenant}
          locale={locale}
          cartTenant={cartTenant}
          product={productInformation.sampleDetails}
        />
      </ProductProvider>
    </AuthProvider>
  );
};

export const shouldRender = (props: SamplesProps): boolean => {
  return !!props?.productInformation?.isEnabled;
};
